import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subscription, timer } from 'rxjs';

import { FeedFileSetService } from '../services/feed-file-set.service';
import { FeedFileSet, FeedFileSetEntry, FeedFileSetStatus, FilesCapability } from '../models/provider.model';
import { FeedFileListComponent } from './feed-file-list.component';
import { ToastService } from '../../../shared/services/toast-service';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { formatBytes } from '../../../shared/utils/format-bytes';
import { relativeTime } from '../../../shared/utils/relative-time';
import { apiErrorMessage, openDownloadTab } from '../util/feed-file-upload';

/** Re-list while any set is still waiting on the provider's verdict. */
const PENDING_POLL_MS = 10_000;

const STATUS_DOT: Record<FeedFileSetStatus, LabelBadgeDot> = {
  PENDING: 'yellow',
  IMPORTED: 'green',
  FAILED: 'red',
};

/**
 * Files for a file-based feed (provider `acceptsFiles`) — the SIRI and generic
 * Files tab. Top: the feed's named files and their sources
 * (`FeedFileListComponent`). Below: the file sets core built from them and
 * delivered (newest 5, newest first, read-only), each with the provider's
 * PENDING/IMPORTED/FAILED verdict and the files as they were in that set
 * (role, metadata, download). Polls while any set is PENDING; a file change
 * re-lists the sets and emits `changed` (the page refreshes the feed's files
 * summary).
 */
@Component({
  selector: 'app-feed-files',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge, FeedFileListComponent],
  templateUrl: './feed-files.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedFilesComponent {
  private readonly fileSetService = inject(FeedFileSetService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly feedId = input.required<string>();
  /** Content only, without the card wrapper and title (for a feed page tab). */
  readonly bare = input(false);
  /** The provider's file rules (descriptor `capabilities.files`), for the role picker and checklist. */
  readonly filesCapability = input<FilesCapability | null>(null);
  readonly providerType = input<string | null>(null);
  readonly changed = output<void>();

  readonly fileSets = signal<FeedFileSet[]>([]);
  readonly isLoading = signal(true);
  /** Set-entry id whose download link is being fetched. */
  readonly downloadingId = signal<string | null>(null);

  readonly formatBytes = formatBytes;
  readonly relativeTime = relativeTime;

  private pollSub: Subscription | null = null;

  private readonly loadOnFeed = effect(() => {
    this.feedId();
    untracked(() => this.fetch());
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.pollSub?.unsubscribe());
  }

  statusDot(status: FeedFileSetStatus): LabelBadgeDot {
    return STATUS_DOT[status];
  }

  metadataEntries(entry: FeedFileSetEntry): [string, string][] {
    return Object.entries(entry.metadata ?? {});
  }

  refresh(): void {
    this.fetch();
  }

  /** A feed file changed — a new set may have been built. */
  onFilesChanged(): void {
    this.fetch();
    this.changed.emit();
  }

  download(set: FeedFileSet, entry: FeedFileSetEntry): void {
    const tab = openDownloadTab();
    this.downloadingId.set(entry.id);
    this.fileSetService
      .getDownloadUrl(this.feedId(), set.id, entry.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.downloadingId.set(null);
          tab.go(res.data.url);
        },
        error: (err: HttpErrorResponse) => {
          this.downloadingId.set(null);
          tab.cancel();
          this.toastService.show(apiErrorMessage(err) ?? this.transloco.translate<string>('providers.feedFiles.toast.downloadFailed'), 'error');
        },
      });
  }

  private fetch(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.fileSetService
      .getAll(this.feedId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.fileSets.set(res.data ?? []);
          this.isLoading.set(false);
          if (this.fileSets().some((s) => s.status === 'PENDING')) {
            this.pollSub = timer(PENDING_POLL_MS).subscribe(() => this.fetch());
          }
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.toastService.show(apiErrorMessage(err) ?? this.transloco.translate<string>('providers.feedFiles.toast.loadFailed'), 'error');
        },
      });
  }
}
